import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Undo2, Redo2, Upload, FileText, AlertTriangle, Sparkles } from 'lucide-react';
import { TopBar } from '../components/TopBar';
import { Footer } from '../components/Footer';
import { DocumentTypeSegmented } from '../components/DocumentTypeSegmented';
import { UploadDropzone } from '../components/UploadDropzone';
import { ShortcutsModal } from '../components/ShortcutsModal';
import { ProgressCircle } from '../components/ProgressCircle';
import { useTheme } from '../hooks/useTheme';
import { useHistory } from '../hooks/useHistory';
import { stashResult } from '../hooks/useResultStore';
import { SAMPLES } from '../data';
import { startAnalysis, pollUntilDone, fetchQuota, extractTags, AnalyseError, type JobStage, type QuotaState } from '../lib/api';
import { normalizePeriod, parsePeriod } from '../lib/period';
import { SECTOR_LABELS, type DocumentType, type Sector } from '../lib/types';
import styles from './InputScreen.module.css';

const STAGE_LABELS: Record<JobStage, string> = {
  queued: 'Queued',
  extract: 'Extracting structure',
  score: 'Scoring dimensions',
  synthesize: 'Synthesizing verdict',
  done: 'Done',
  error: 'Error',
};


export function InputScreen() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { items: history, push } = useHistory();

  const [docType, setDocType] = useState<DocumentType>('auto');
  const [mode, setMode] = useState<'paste' | 'upload'>('paste');
  const [text, setText] = useState('');
  const [ticker, setTicker] = useState('');
  const [period, setPeriod] = useState('');
  const [sector, setSector] = useState<Sector | ''>('');
  // Track which fields were filled by auto-detect so we can show a "Detected"
  // badge — cleared the moment the user edits the field.
  const [autoDetected, setAutoDetected] = useState<Set<'ticker' | 'period' | 'sector'>>(new Set());
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [stage, setStage] = useState<JobStage>('queued');
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaState | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Fetch quota once on mount so we can render "X / 5 analyses used".
  useEffect(() => {
    void fetchQuota().then(setQuota).catch(() => { /* non-fatal */ });
  }, []);

  // Undo / redo stacks for the textarea
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const lastValueRef = useRef('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isCmd = e.metaKey || e.ctrlKey;
      if (isCmd && e.key === 'Enter') {
        e.preventDefault();
        runAnalysis();
      }
      if (e.key === '?' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setShortcutsOpen(true);
      }
      if (e.key === '/' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        textareaRef.current?.focus();
      }
      if ((e.key === 't' || e.key === 'T') && document.activeElement?.tagName !== 'TEXTAREA') {
        toggle();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, docType, mode]);

  const onTextChange = (next: string) => {
    if (lastValueRef.current !== text) {
      setUndoStack((s) => [...s, lastValueRef.current]);
    } else {
      setUndoStack((s) => [...s, text]);
    }
    setRedoStack([]);
    lastValueRef.current = text;
    setText(next);
  };

  const undo = () => {
    setUndoStack((s) => {
      if (!s.length) return s;
      const prev = s[s.length - 1];
      setRedoStack((r) => [...r, text]);
      setText(prev ?? '');
      return s.slice(0, -1);
    });
  };

  const redo = () => {
    setRedoStack((s) => {
      if (!s.length) return s;
      const next = s[s.length - 1];
      setUndoStack((u) => [...u, text]);
      setText(next ?? '');
      return s.slice(0, -1);
    });
  };

  const sample = (slug: string) => () => {
    const target = SAMPLES.find((s) => s.slug === slug);
    if (!target) return;
    push(target);
    navigate(`/result/${target.slug}`);
  };

  const runAnalysis = async () => {
    if (analysing) return;
    setError(null);
    if (!text.trim()) {
      setError('Paste a document or pick a sample before analysing.');
      return;
    }
    if (text.trim().length < 200) {
      setError('Document is too short to analyse — paste at least a few paragraphs.');
      return;
    }

    setAnalysing(true);
    setStage('queued');
    abortRef.current = new AbortController();

    // Normalise the optional period before sending; if it doesn't parse
    // we still ship the raw string so the backend can record it as-is
    // (won't participate in cohort joins, but won't break either).
    const normalisedPeriod = period ? normalizePeriod(period) : '';
    const periodParsed = normalisedPeriod ? parsePeriod(normalisedPeriod) : null;
    if (period && !periodParsed) {
      setError('Period not recognized — try "2026-Q1" or "2026-FY".');
      setAnalysing(false);
      return;
    }

    try {
      const { jobId, quota: afterStart } = await startAnalysis({
        text,
        documentType: docType,
        ticker: ticker.trim() || undefined,
        period: normalisedPeriod || undefined,
        sector: sector || undefined,
        signal: abortRef.current.signal,
      });
      if (afterStart) setQuota(afterStart);
      // Wait for the full pipeline to finish before navigating — progress
      // is shown via the circular indicator while we wait, then the user
      // lands on a fully-populated result page.
      const result = await pollUntilDone({
        jobId,
        signal: abortRef.current.signal,
        onProgress: (s) => setStage(s.stage),
      });
      stashResult(result);
      push(result);
      navigate(`/result/${result.slug}`);
    } catch (err) {
      const message =
        err instanceof AnalyseError ? err.message :
        err instanceof Error ? err.message :
        'Analysis failed.';
      setError(message);
      // If the error came back with quota state (e.g. 429), refresh the meter.
      const maybeQuota = (err as AnalyseError & { quota?: QuotaState }).quota;
      if (maybeQuota) setQuota(maybeQuota);
    } finally {
      setAnalysing(false);
      abortRef.current = null;
    }
  };

  const cancelAnalysis = () => {
    abortRef.current?.abort();
  };

  const onAutoDetect = async () => {
    if (detecting || !text.trim() || text.trim().length < 200) return;
    setDetecting(true);
    setDetectError(null);
    try {
      const tags = await extractTags(text);
      const newAuto = new Set(autoDetected);
      // Only fill empty fields — don't overwrite what the user typed.
      if (tags.ticker && !ticker) {
        setTicker(tags.ticker);
        newAuto.add('ticker');
      }
      if (tags.period && !period) {
        setPeriod(tags.period);
        newAuto.add('period');
      }
      if (tags.sector && !sector) {
        setSector(tags.sector);
        newAuto.add('sector');
      }
      setAutoDetected(newAuto);
      const filled = (tags.ticker ? 1 : 0) + (tags.period ? 1 : 0) + (tags.sector ? 1 : 0);
      if (filled === 0) setDetectError('Couldn\'t detect any tags from this document — fill them manually.');
    } catch (err) {
      const message = err instanceof AnalyseError ? err.message :
        err instanceof Error ? err.message : 'Auto-detect failed.';
      setDetectError(message);
    } finally {
      setDetecting(false);
    }
  };

  // Helpers that clear the "auto-detected" badge as soon as the user edits.
  const editTicker = (v: string) => {
    setTicker(v);
    if (autoDetected.has('ticker')) {
      const next = new Set(autoDetected);
      next.delete('ticker');
      setAutoDetected(next);
    }
  };
  const editPeriod = (v: string) => {
    setPeriod(v);
    if (autoDetected.has('period')) {
      const next = new Set(autoDetected);
      next.delete('period');
      setAutoDetected(next);
    }
  };
  const editSector = (v: Sector | '') => {
    setSector(v);
    if (autoDetected.has('sector')) {
      const next = new Set(autoDetected);
      next.delete('sector');
      setAutoDetected(next);
    }
  };

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const charCount = text.length;

  return (
    <div className={styles.page}>
      <TopBar
        historyCount={history.length}
        theme={theme}
        onShortcuts={() => setShortcutsOpen(true)}
        onToggleTheme={toggle}
      />

      <main className={styles.main} id="main">
        <div className={styles.heroBlock}>
          <span className={styles.eyebrow}>Sentiment Analysis</span>
          <h1 className={styles.headline}>
            Read between the lines of any <span className={styles.accent}>earnings call, 10-K, or credit memo.</span>
          </h1>
          <p className={styles.subhead}>
            Paste the source. Get a verdict, the linguistic fingerprint, and the evidence that backs it — in seconds.
          </p>
          {quota && (
            <div
              className={[
                styles.quotaMeter,
                quota.remaining === 0 ? styles.quotaExhausted :
                quota.remaining <= 1 ? styles.quotaLow : '',
              ].filter(Boolean).join(' ')}
              title={`Resets ${new Date(quota.resetAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
            >
              <div className={styles.quotaTrack}>
                <div
                  className={styles.quotaFill}
                  style={{ width: `${(quota.used / quota.limit) * 100}%` }}
                />
              </div>
              <span className={[styles.quotaLabel, 'mono'].join(' ')}>
                {quota.used} / {quota.limit} analyses used this month
              </span>
            </div>
          )}
        </div>

        <div className={styles.controls}>
          <div className={styles.controlField}>
            <label className={styles.controlLabel}>Document Type</label>
            <DocumentTypeSegmented value={docType} onChange={setDocType} />
          </div>
          <div className={styles.controlField}>
            <label className={styles.controlLabel}>Source</label>
            <div className={styles.modeToggle}>
              <button
                type="button"
                className={[styles.modeBtn, mode === 'paste' ? styles.modeActive : ''].join(' ')}
                onClick={() => setMode('paste')}
              >
                <FileText size={14} strokeWidth={1.5} aria-hidden="true" />
                Paste Text
              </button>
              <button
                type="button"
                className={[styles.modeBtn, mode === 'upload' ? styles.modeActive : ''].join(' ')}
                onClick={() => setMode('upload')}
              >
                <Upload size={14} strokeWidth={1.5} aria-hidden="true" />
                Upload Document
              </button>
            </div>
          </div>
        </div>

        <div className={styles.metaRowFields}>
          <div className={styles.metaField}>
            <label className={styles.controlLabel} htmlFor="meta-ticker">
              Ticker <span className={styles.optional}>(optional)</span>
              {autoDetected.has('ticker') && <span className={styles.detectedBadge} title="Detected from document">Detected</span>}
            </label>
            <input
              id="meta-ticker"
              type="text"
              className={styles.metaInput}
              placeholder="e.g. AAPL"
              value={ticker}
              onChange={(e) => editTicker(e.target.value.toUpperCase().slice(0, 10))}
              autoComplete="off"
            />
          </div>
          <div className={styles.metaField}>
            <label className={styles.controlLabel} htmlFor="meta-period">
              Period <span className={styles.optional}>(optional)</span>
              {autoDetected.has('period') && <span className={styles.detectedBadge} title="Detected from document">Detected</span>}
            </label>
            <input
              id="meta-period"
              type="text"
              className={styles.metaInput}
              placeholder="e.g. 2026-Q1 or 2026-FY"
              value={period}
              onChange={(e) => editPeriod(e.target.value)}
              onBlur={(e) => {
                const v = e.target.value ? normalizePeriod(e.target.value) : '';
                setPeriod(v);
              }}
              autoComplete="off"
            />
          </div>
          <div className={styles.metaField}>
            <label className={styles.controlLabel} htmlFor="meta-sector">
              Sector <span className={styles.optional}>(optional)</span>
              {autoDetected.has('sector') && <span className={styles.detectedBadge} title="Detected from document">Detected</span>}
            </label>
            <select
              id="meta-sector"
              className={styles.metaSelect}
              value={sector}
              onChange={(e) => editSector((e.target.value || '') as Sector | '')}
            >
              <option value="">Auto-detect</option>
              {(Object.keys(SECTOR_LABELS) as Sector[]).map((k) => (
                <option key={k} value={k}>{SECTOR_LABELS[k]}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.detectRow}>
          <button
            type="button"
            className={styles.detectBtn}
            onClick={onAutoDetect}
            disabled={detecting || text.trim().length < 200}
            aria-busy={detecting}
            title="Read the document and pre-fill empty tags"
          >
            <Sparkles size={14} strokeWidth={1.5} aria-hidden="true" />
            <span>{detecting ? 'Detecting…' : 'Auto-detect from document'}</span>
          </button>
          <p className={styles.metaHint}>
            Tags enable cross-cutting comparisons: ticker drives prior-quarter diffs, period &amp; sector drive peer cohorts. Optional, but each untagged analysis is invisible to the corpus.
          </p>
        </div>
        {detectError && (
          <p role="alert" className={styles.detectError}>{detectError}</p>
        )}

        <div className={styles.inputCard}>
          {mode === 'paste' ? (
            <>
              <div className={styles.textareaChrome}>
                <div className={styles.textareaActions}>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={undo}
                    aria-label="Undo"
                    disabled={!undoStack.length}
                  >
                    <Undo2 size={14} strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={redo}
                    aria-label="Redo"
                    disabled={!redoStack.length}
                  >
                    <Redo2 size={14} strokeWidth={1.5} />
                  </button>
                </div>
                <div className={styles.samples}>
                  <span className={styles.samplesLabel}>Sample:</span>
                  <button type="button" className={styles.sampleBtn} onClick={sample('sample-earnings-call')}>
                    Earnings Call
                  </button>
                  <button type="button" className={styles.sampleBtn} onClick={sample('sample-10k-mdna')}>
                    10-K MD&amp;A
                  </button>
                  <button type="button" className={styles.sampleBtn} onClick={sample('sample-credit-report')}>
                    Credit Report
                  </button>
                </div>
              </div>

              <textarea
                ref={textareaRef}
                className={styles.textarea}
                placeholder="Paste the document text here. The analyser handles up to ~120k tokens — full earnings calls and 10-K MD&amp;A sections work fine."
                value={text}
                onChange={(e) => onTextChange(e.target.value)}
                aria-label="Document text"
                rows={12}
              />

              <div className={styles.metaRow}>
                <span className={[styles.metaItem, 'mono'].join(' ')}>
                  {wordCount.toLocaleString()} words · {charCount.toLocaleString()} chars
                </span>
                <span className={styles.shortcutHint}>
                  <kbd className={styles.kbd}>Ctrl</kbd>
                  <span className={styles.plus}>+</span>
                  <kbd className={styles.kbd}>Enter</kbd>
                  <span> to analyse</span>
                </span>
              </div>
            </>
          ) : (
            <UploadDropzone
              onText={(t) => {
                setText(t);
                setMode('paste');
                setError(null);
              }}
              onError={setError}
            />
          )}
        </div>

        {error && (
          <div role="alert" className={styles.errorBanner}>
            <AlertTriangle size={16} strokeWidth={1.5} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.actionRow}>
          {analysing ? (
            <div className={styles.analysingWrap}>
              <ProgressCircle stage={stage} size={140} />
              <div className={styles.analysingText}>
                <span className={styles.analysingHeadline}>{STAGE_LABELS[stage]}</span>
                <span className={styles.analysingNote}>
                  Three sequential passes against Claude. Typical earnings calls finish in 20–30s; long 10-Ks can take a minute or more.
                </span>
              </div>
              <button type="button" className={styles.cancelBtn} onClick={cancelAnalysis}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className={styles.primaryAction} onClick={runAnalysis}>
              Analyse Sentiment
            </button>
          )}
        </div>

        <p className={styles.tagline}>
          Five-dimension scoring on tone, posture, directness, Q&amp;A discipline and quantification — every signal cite-aligned to source paragraphs.
        </p>
      </main>

      <Footer onShortcuts={() => setShortcutsOpen(true)} />
      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
